import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// A union would make AutoForm drop the `type: "date"` input prop, so an
// optional date is a plain string whose pattern also admits "" (cleared field).
const OPTIONAL_ISO_DATE = /^(\d{4}-\d{2}-\d{2})?$/;

export const costSubscriptionFormSchema = z
  .object({
    id: z
      .string()
      .nullish()
      .superRefine(fieldConfig({ inputProps: { disabled: true } })),
    name: z
      .string()
      .min(1)
      .max(200)
      .superRefine(fieldConfig({ label: "Name" })),
    vendor: z
      .string()
      .min(1)
      .max(100)
      .superRefine(fieldConfig({ label: "Vendor" })),
    // The shared NumberField is a plain `<input type="number">` without
    // `valueAsNumber`, so the price arrives as a string: coerce it. AutoForm
    // drops an emptied field before validating, so an empty price is a clear
    // "Enter a price" error rather than a silent 0.
    monthly_usd: z.coerce
      .number({ invalid_type_error: "Enter a price" })
      .min(0, "Must not be negative")
      .refine(
        (value) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-6,
        "At most two decimals",
      )
      .superRefine(
        fieldConfig({
          label: "Monthly price (USD)",
          inputProps: { step: "0.01", min: 0 },
        }),
      ),
    starts_on: z
      .string()
      .regex(ISO_DATE, "Start must be YYYY-MM-DD")
      .superRefine(
        fieldConfig({ label: "Starts on", inputProps: { type: "date" } }),
      ),
    ends_on: z
      .string()
      .regex(OPTIONAL_ISO_DATE, "End must be YYYY-MM-DD")
      .nullish()
      .superRefine(
        fieldConfig({
          label: "Ends on (empty = active)",
          inputProps: { type: "date" },
        }),
      ),
    note: z
      .string()
      .max(1000)
      .nullish()
      .superRefine(fieldConfig({ label: "Note", fieldType: "textarea" })),
  })
  .superRefine((value, ctx) => {
    if (value.ends_on && value.ends_on < value.starts_on) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ends_on"],
        message: "End must not be before the start",
      });
    }
  });

export type CostSubscriptionFormType = z.infer<
  typeof costSubscriptionFormSchema
>;

/**
 * The API forbids extra fields, so only the editable columns go out (id rides
 * in the URL). An empty `ends_on` or `note` is sent as an explicit null: on
 * PATCH that is what clears the column, on POST it is the default.
 */
export const toCostSubscriptionPayload = (
  values: CostSubscriptionFormType,
) => ({
  name: values.name,
  vendor: values.vendor,
  monthly_usd: values.monthly_usd.toFixed(2),
  starts_on: values.starts_on,
  ends_on: values.ends_on || null,
  note: values.note || null,
});
